import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Slider } from '../components/ui/slider';
import {
  Calculator, Thermometer, Zap, Gauge, Droplets, Wind, AlertTriangle, Send, ChevronRight,
  Settings, Activity, BarChart3, Info, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from 'recharts';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function MetricCard({ icon: Icon, label, value, unit, accent }) {
  return (
    <div className="p-3 rounded-lg border" style={{ background: accent ? 'rgba(14,138,138,0.05)' : 'var(--surface-1)' }}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color: 'var(--brand-teal)' }} />
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk', color: accent ? 'var(--brand-teal)' : 'var(--brand-navy)' }}>{value}</span>
        {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
      </div>
    </div>
  );
}

function SpecRow({ label, value, unit, highlight }) {
  return (
    <tr className={highlight ? 'bg-[rgba(14,138,138,0.04)]' : ''}>
      <td className="py-2 px-3 text-sm text-muted-foreground whitespace-nowrap">{label}</td>
      <td className="py-2 px-3 text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>
        {value}{unit && <span className="text-xs text-muted-foreground ml-1">{unit}</span>}
      </td>
    </tr>
  );
}

export default function ChillerSelector() {
  // Inputs
  const [inputs, setInputs] = useState({
    capacity_tr: 100,
    leaving_fluid_temp: 5,
    refrigerant: 'R134a',
    ecwt: 30,
    system_type: 'chilled_water',
    evaporator_type: 'flooded',
    fluid_type: 'water',
    compressor_type: 'twin',
    motor_efficiency_class: 'IE3',
    economizer: false,
  });

  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('input');

  // Send to sales
  const [salesDialogOpen, setSalesDialogOpen] = useState(false);
  const [salesForm, setSalesForm] = useState({ user_name: '', user_email: '', user_company: '', user_phone: '' });
  const [sending, setSending] = useState(false);

  const updateInput = (field, value) => {
    setInputs(prev => {
      const next = { ...prev, [field]: value };
      // Auto-set fluid_type based on system_type
      if (field === 'system_type') {
        if (value === 'chilled_water') next.fluid_type = 'water';
        else if (value === 'brine' && next.fluid_type === 'water') next.fluid_type = 'glycol';
      }
      // Auto-recommend economizer for brine with low temp
      if (field === 'leaving_fluid_temp' || field === 'system_type') {
        if (next.system_type === 'brine' && next.leaving_fluid_temp < -5) {
          next.economizer = true;
        }
      }
      return next;
    });
  };

  const handleCalculate = useCallback(async () => {
    setCalculating(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/chiller/calculate`, inputs);
      if (res.data.success) {
        setResult(res.data.result);
        setActiveTab('results');
        toast.success('Calculation complete');
      } else {
        setError(res.data.error || 'Calculation failed');
        toast.error(res.data.error || 'Calculation failed');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Calculation error';
      setError(msg);
      toast.error(msg);
    } finally {
      setCalculating(false);
    }
  }, [inputs]);

  const handleSendToSales = async () => {
    if (!salesForm.user_email) { toast.error('Email is required'); return; }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/chiller/send-to-sales`, {
        ...salesForm,
        result: result,
      });
      toast.success('Selection sent to sales team!');
      setSalesDialogOpen(false);
    } catch {
      toast.error('Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const r = result; // shorthand

  return (
    <div className="page-transition">
      {/* Header */}
      <section className="py-8 sm:py-10" style={{ background: 'linear-gradient(135deg, var(--brand-navy) 0%, #0f2847 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-white">Chiller Selection Tool</span>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(14,138,138,0.3)' }}>
              <Calculator className="w-5 h-5 text-[var(--brand-teal-2)]" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Space Grotesk' }}>
                Advanced Thermodynamic Selection Tool
              </h1>
              <p className="text-xs text-slate-400">Physics-based engine powered by CoolProp | Water-cooled screw chillers | 20–300 TR</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-6" style={{ background: 'var(--surface-0)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full justify-start flex-wrap gap-1 bg-transparent border-b rounded-none pb-0 h-auto mb-6">
              <TabsTrigger value="input" className="rounded-b-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-teal)] gap-1.5">
                <Settings className="w-3.5 h-3.5" /> Load Input
              </TabsTrigger>
              <TabsTrigger value="results" className="rounded-b-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-teal)] gap-1.5" disabled={!result}>
                <Activity className="w-3.5 h-3.5" /> Results
              </TabsTrigger>
              <TabsTrigger value="iplv" className="rounded-b-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-teal)] gap-1.5" disabled={!result}>
                <BarChart3 className="w-3.5 h-3.5" /> NPLV / IPLV Curve
              </TabsTrigger>
            </TabsList>

            {/* ═══ TAB: INPUT ═══ */}
            <TabsContent value="input">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Load Requirements */}
                <Card style={{ background: 'var(--surface-1)' }}>
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-sm mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>
                      <Thermometer className="w-4 h-4" style={{ color: 'var(--brand-teal)' }} /> Load Requirements
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-xs">Cooling Capacity (TR)</Label>
                        <div className="flex items-center gap-3">
                          <Slider
                            data-testid="capacity-slider"
                            min={20} max={300} step={5}
                            value={[inputs.capacity_tr]}
                            onValueChange={([v]) => updateInput('capacity_tr', v)}
                            className="flex-1"
                          />
                          <Input
                            data-testid="capacity-input"
                            type="number" min={20} max={300}
                            value={inputs.capacity_tr}
                            onChange={(e) => updateInput('capacity_tr', Number(e.target.value))}
                            className="w-20 text-center"
                          />
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">20–300 TR ({(inputs.capacity_tr * 3.5168).toFixed(0)} kW)</p>
                      </div>

                      <div>
                        <Label className="text-xs">Leaving Fluid Temperature (°C)</Label>
                        <div className="flex items-center gap-3">
                          <Slider
                            data-testid="lwt-slider"
                            min={-20} max={5} step={1}
                            value={[inputs.leaving_fluid_temp]}
                            onValueChange={([v]) => updateInput('leaving_fluid_temp', v)}
                            className="flex-1"
                          />
                          <Input
                            data-testid="lwt-input"
                            type="number" min={-20} max={5}
                            value={inputs.leaving_fluid_temp}
                            onChange={(e) => updateInput('leaving_fluid_temp', Number(e.target.value))}
                            className="w-20 text-center"
                          />
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">+5°C to -20°C</p>
                      </div>

                      <div>
                        <Label className="text-xs">Refrigerant</Label>
                        <Select value={inputs.refrigerant} onValueChange={(v) => updateInput('refrigerant', v)}>
                          <SelectTrigger data-testid="refrigerant-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="R134a">R134a (Medium Temp)</SelectItem>
                            <SelectItem value="R404A">R404A (Low Temp)</SelectItem>
                            <SelectItem value="R507A">R507A (Low Temp)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs">Entering Condenser Water Temp (°C)</Label>
                        <div className="flex items-center gap-3">
                          <Slider
                            min={25} max={35} step={1}
                            value={[inputs.ecwt]}
                            onValueChange={([v]) => updateInput('ecwt', v)}
                            className="flex-1"
                          />
                          <Input
                            type="number" min={25} max={35}
                            value={inputs.ecwt}
                            onChange={(e) => updateInput('ecwt', Number(e.target.value))}
                            className="w-20 text-center"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* System Configuration */}
                <Card style={{ background: 'var(--surface-1)' }}>
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-sm mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>
                      <Settings className="w-4 h-4" style={{ color: 'var(--brand-teal)' }} /> System Configuration
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-xs">System Type</Label>
                        <Select value={inputs.system_type} onValueChange={(v) => updateInput('system_type', v)}>
                          <SelectTrigger data-testid="system-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="chilled_water">Chilled Water</SelectItem>
                            <SelectItem value="brine">Brine</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs">Evaporator Type</Label>
                        <Select value={inputs.evaporator_type} onValueChange={(v) => updateInput('evaporator_type', v)}>
                          <SelectTrigger data-testid="evap-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="flooded">Flooded</SelectItem>
                            <SelectItem value="dx">Direct Expansion (DX)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {inputs.evaporator_type === 'flooded' ? 'SST = LWT - 3K (higher efficiency)' : 'SST = LWT - 5K'}
                        </p>
                      </div>

                      {inputs.system_type === 'brine' && (
                        <div>
                          <Label className="text-xs">Fluid Type</Label>
                          <Select value={inputs.fluid_type} onValueChange={(v) => updateInput('fluid_type', v)}>
                            <SelectTrigger data-testid="fluid-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="glycol">Glycol 30% (Cp = 3.6 kJ/kg·K)</SelectItem>
                              <SelectItem value="methanol">Methanol 20% (Cp = 3.8 kJ/kg·K)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <div>
                        <Label className="text-xs">Compressor Type</Label>
                        <Select value={inputs.compressor_type} onValueChange={(v) => updateInput('compressor_type', v)}>
                          <SelectTrigger data-testid="comp-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="twin">Twin Screw (higher eff.)</SelectItem>
                            <SelectItem value="single">Single Screw</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs">Motor Efficiency</Label>
                        <Select value={inputs.motor_efficiency_class} onValueChange={(v) => updateInput('motor_efficiency_class', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="IE3">IE3 (95.0%)</SelectItem>
                            <SelectItem value="IE4">IE4 (96.5%)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Separator />

                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-xs">Economizer</Label>
                          <p className="text-[10px] text-muted-foreground">Boosts capacity at high PR</p>
                        </div>
                        <Switch
                          data-testid="economizer-switch"
                          checked={inputs.economizer}
                          onCheckedChange={(v) => updateInput('economizer', v)}
                        />
                      </div>
                      {inputs.system_type === 'brine' && inputs.leaving_fluid_temp < -5 && (
                        <div className="flex items-start gap-2 text-xs p-2 rounded" style={{ background: 'rgba(14,138,138,0.08)', color: 'var(--brand-teal)' }}>
                          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          <span>Economizer auto-recommended for brine systems below -5°C</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {error && (
                <div className="mt-4 p-3 rounded-lg border border-red-200 bg-red-50 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <Button
                  data-testid="calculate-button"
                  onClick={handleCalculate}
                  disabled={calculating}
                  className="text-white px-8"
                  style={{ background: 'var(--brand-teal)' }}
                >
                  {calculating ? 'Calculating...' : 'Calculate Selection'}
                  <Calculator className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </TabsContent>

            {/* ═══ TAB: RESULTS ═══ */}
            <TabsContent value="results">
              {r && (
                <div>
                  {/* PR Warning */}
                  {r.pr_warning === 'warning' && (
                    <div className="mb-4 p-3 rounded-lg border border-yellow-300 bg-yellow-50 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 shrink-0" />
                      <p className="text-sm text-yellow-800">Pressure ratio {r.pressure_ratio} is above 9.0 — approaching operational limit. Verify with manufacturer.</p>
                    </div>
                  )}

                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                    <MetricCard icon={Zap} label="Capacity" value={r.capacity_kw} unit="kW" />
                    <MetricCard icon={Gauge} label="COP" value={r.cop} accent />
                    <MetricCard icon={Activity} label="kW/TR" value={r.kw_per_tr} />
                    <MetricCard icon={Zap} label="Motor Power" value={r.motor_power_kw} unit="kW" />
                    <MetricCard icon={Thermometer} label="NPLV/IPLV" value={r.nplv} accent />
                    <MetricCard icon={Gauge} label="PR" value={r.pressure_ratio} />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Thermodynamic Results */}
                    <Card style={{ background: 'var(--surface-1)' }}>
                      <CardContent className="p-5">
                        <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>
                          <Thermometer className="w-4 h-4" style={{ color: 'var(--brand-teal)' }} /> Thermodynamic Summary
                        </h3>
                        <table className="w-full">
                          <tbody>
                            <SpecRow label="Cooling Capacity" value={`${r.capacity_tr} TR / ${r.capacity_kw}`} unit="kW" highlight />
                            <SpecRow label="Evaporator SST" value={r.evap_sst} unit="°C" />
                            <SpecRow label="Condenser SST" value={r.cond_sst} unit="°C" />
                            <SpecRow label="Pressure Ratio" value={r.pressure_ratio} highlight />
                            <SpecRow label="Isentropic Efficiency" value={(r.isentropic_efficiency * 100).toFixed(1)} unit="%" />
                            <SpecRow label="Motor Efficiency" value={(r.motor_efficiency * 100).toFixed(1)} unit="%" />
                            <SpecRow label="Discharge Temp" value={r.discharge_temp} unit="°C" />
                            <SpecRow label="Compressors" value={r.num_compressors} highlight />
                            <SpecRow label="Power per Compressor" value={r.power_per_compressor_kw} unit="kW" />
                          </tbody>
                        </table>
                      </CardContent>
                    </Card>

                    {/* Flow & Performance */}
                    <Card style={{ background: 'var(--surface-1)' }}>
                      <CardContent className="p-5">
                        <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>
                          <Droplets className="w-4 h-4" style={{ color: 'var(--brand-teal)' }} /> Flow & Mass Data
                        </h3>
                        <table className="w-full">
                          <tbody>
                            <SpecRow label="Evaporator Flow" value={r.evaporator_flow_m3h} unit="m³/hr" highlight />
                            <SpecRow label="Condenser Flow" value={r.condenser_flow_m3h} unit="m³/hr" />
                            <SpecRow label="Condenser Duty" value={r.condenser_duty_kw} unit="kW" highlight />
                            <SpecRow label="Mass Flow (LP)" value={r.mass_flow_lp} unit="kg/s" />
                            <SpecRow label="Mass Flow (HP)" value={r.mass_flow_hp} unit="kg/s" />
                            {r.economizer && <SpecRow label="Mass Flow (ECO)" value={r.mass_flow_eco} unit="kg/s" highlight />}
                            <SpecRow label="Evap Pressure" value={r.p_evap_bar} unit="bar" />
                            <SpecRow label="Cond Pressure" value={r.p_cond_bar} unit="bar" />
                          </tbody>
                        </table>

                        {r.economizer && (
                          <div className="mt-3 p-2 rounded text-xs" style={{ background: 'rgba(14,138,138,0.06)' }}>
                            <span className="font-medium" style={{ color: 'var(--brand-teal)' }}>Economizer Active</span>
                            <span className="text-muted-foreground"> — Boosted capacity: {r.eco_capacity_kw} kW</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Enthalpy reference */}
                  <Card className="mt-4" style={{ background: 'var(--surface-1)' }}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Info className="w-3.5 h-3.5" style={{ color: 'var(--brand-teal)' }} />
                        <span className="text-xs font-medium" style={{ color: 'var(--brand-navy)' }}>Cycle State Points (CoolProp)</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                        <div><span className="text-muted-foreground">h1 (Evap Vapor):</span> <strong>{r.h1} kJ/kg</strong></div>
                        <div><span className="text-muted-foreground">h2s (Isen. Discharge):</span> <strong>{r.h2s} kJ/kg</strong></div>
                        <div><span className="text-muted-foreground">h3 (Cond Liquid):</span> <strong>{r.h3} kJ/kg</strong></div>
                        <div><span className="text-muted-foreground">h4 (After Expansion):</span> <strong>{r.h4} kJ/kg</strong></div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Actions */}
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Dialog open={salesDialogOpen} onOpenChange={setSalesDialogOpen}>
                      <DialogTrigger asChild>
                        <Button data-testid="send-to-sales-button" className="text-white" style={{ background: 'var(--brand-teal)' }}>
                          <Send className="w-4 h-4 mr-2" /> Send to Sales
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Send Selection to Sales</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3 mt-2">
                          <div><Label className="text-xs">Name</Label><Input value={salesForm.user_name} onChange={(e) => setSalesForm(p => ({ ...p, user_name: e.target.value }))} /></div>
                          <div><Label className="text-xs">Email *</Label><Input type="email" data-testid="sales-email-input" value={salesForm.user_email} onChange={(e) => setSalesForm(p => ({ ...p, user_email: e.target.value }))} required /></div>
                          <div><Label className="text-xs">Company</Label><Input value={salesForm.user_company} onChange={(e) => setSalesForm(p => ({ ...p, user_company: e.target.value }))} /></div>
                          <div><Label className="text-xs">Phone</Label><Input value={salesForm.user_phone} onChange={(e) => setSalesForm(p => ({ ...p, user_phone: e.target.value }))} /></div>
                          <Button onClick={handleSendToSales} disabled={sending} className="w-full text-white" style={{ background: 'var(--brand-teal)' }}>
                            {sending ? 'Sending...' : 'Submit to Sales Team'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Button variant="outline" onClick={() => setActiveTab('input')}>
                      Modify Inputs
                    </Button>
                    <Button variant="outline" onClick={() => setActiveTab('iplv')}>
                      View NPLV/IPLV Curve
                    </Button>
                  </div>

                  <p className="mt-6 text-[10px] text-muted-foreground italic">
                    Disclaimer: Preliminary engineering selection only. Accurate data will vary — always verify with supplier. Final design subject to detailed engineering review.
                  </p>
                </div>
              )}
            </TabsContent>

            {/* ═══ TAB: IPLV CURVE ═══ */}
            <TabsContent value="iplv">
              {r && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="font-semibold text-base" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Part-Load Performance (NPLV / IPLV)</h3>
                      <p className="text-xs text-muted-foreground">AHRI weighting: 1% × 100% + 42% × 75% + 45% × 50% + 12% × 25%</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-teal)' }}>{r.nplv}</div>
                      <div className="text-xs text-muted-foreground">NPLV / IPLV</div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* COP Curve */}
                    <Card style={{ background: 'var(--surface-1)' }}>
                      <CardContent className="p-5">
                        <h4 className="font-semibold text-sm mb-3" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>COP vs Load</h4>
                        <ResponsiveContainer width="100%" height={280}>
                          <LineChart data={r.part_load_data}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis dataKey="load_pct" tickFormatter={(v) => `${v}%`} fontSize={11} />
                            <YAxis fontSize={11} domain={['auto', 'auto']} />
                            <Tooltip
                              contentStyle={{ background: 'var(--surface-1)', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }}
                              formatter={(value, name) => [value, name === 'cop' ? 'COP' : name]}
                              labelFormatter={(v) => `Load: ${v}%`}
                            />
                            <Line type="monotone" dataKey="cop" stroke="var(--brand-teal)" strokeWidth={2} dot={{ fill: 'var(--brand-teal)', r: 4 }} name="COP" />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Power Curve */}
                    <Card style={{ background: 'var(--surface-1)' }}>
                      <CardContent className="p-5">
                        <h4 className="font-semibold text-sm mb-3" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Power vs Load</h4>
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart data={r.part_load_data}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis dataKey="load_pct" tickFormatter={(v) => `${v}%`} fontSize={11} />
                            <YAxis fontSize={11} />
                            <Tooltip
                              contentStyle={{ background: 'var(--surface-1)', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }}
                              formatter={(value) => [`${value} kW`, 'Power']}
                              labelFormatter={(v) => `Load: ${v}%`}
                            />
                            <Bar dataKey="power_kw" fill="var(--brand-navy)" radius={[4, 4, 0, 0]} name="Power (kW)" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Part Load Data Table */}
                  <Card className="mt-4" style={{ background: 'var(--surface-1)' }}>
                    <CardContent className="p-5">
                      <h4 className="font-semibold text-sm mb-3" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Part-Load Data</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full zebra-table spec-table">
                          <thead>
                            <tr style={{ background: 'var(--surface-2)' }}>
                              <th className="text-left p-2 text-xs font-semibold" style={{ color: 'var(--brand-navy)' }}>Load</th>
                              <th className="text-left p-2 text-xs font-semibold">AHRI Weight</th>
                              <th className="text-left p-2 text-xs font-semibold">Capacity (kW)</th>
                              <th className="text-left p-2 text-xs font-semibold">Power (kW)</th>
                              <th className="text-left p-2 text-xs font-semibold">COP</th>
                              <th className="text-left p-2 text-xs font-semibold">PR</th>
                              <th className="text-left p-2 text-xs font-semibold">ECWT (°C)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.part_load_data.map((row, i) => {
                              const weights = { 100: '1%', 75: '42%', 50: '45%', 25: '12%' };
                              return (
                                <tr key={row.load_pct}>
                                  <td className="p-2 text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>{row.load_pct}%</td>
                                  <td className="p-2 text-sm text-muted-foreground">{weights[row.load_pct]}</td>
                                  <td className="p-2 text-sm">{row.capacity_kw}</td>
                                  <td className="p-2 text-sm">{row.power_kw}</td>
                                  <td className="p-2 text-sm font-medium" style={{ color: 'var(--brand-teal)' }}>{row.cop}</td>
                                  <td className="p-2 text-sm">{row.pr}</td>
                                  <td className="p-2 text-sm">{row.ecwt}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  <p className="mt-4 text-[10px] text-muted-foreground italic">
                    Note: Using fixed ECWT = {r.ecwt}°C. Per AHRI standard, this is classified as NPLV (Non-Standard Part Load Value). Standard IPLV uses declining ECWT at part load.
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
